/**
 * @file EmailUndoPage.tsx
 * @brief `/auth/email-undo?token=…`: the link in the "Your email was
 *        changed" notice sent to the OLD address (B34, ADR-059). Public, no
 *        sign-in; works signed in or out.
 *
 * @details
 * - Opening the page only reads (GET): mail scanners open links, so the undo
 *   is a POST from the button, never on load.
 * - The token is read once into state and then taken out of the address bar
 *   (history replace), so it is not left in history, a copied URL or a
 *   referrer. It is never logged or stored.
 * - Undo signs out every session of the account (the server bumps its token
 *   version), so a session in this browser is cleared too, with the trusted
 *   2FA device. Then "Reset password": whoever changed the email may know it.
 * - 410 LINK_INVALID / 409 EMAIL_TAKEN / 429: the server's sentence.
 *
 * @references
 * - docs/product/email-change-reauth.md "Undo link"
 * - ./profile/emailChange.ts (calls), BACKEND/src/features/auth/routes.js
 *
 * @author DES (prepared with Claude)
 * @date 26-10-2026
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Check, KeyRound, LoaderCircle, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ServerError } from "@/features/auth/register/registerUi";
import { createEmailUndoApi, parseUndoInfo, type EmailUndoInfo } from "@/features/profile/emailChange";
import { forgetTrustedDevice } from "@/features/auth/twoFactor/twoFactorRules";
import { useAuth } from "@/shared/auth/auth-context";
import { env } from "@/shared/config/env";

type Api = ReturnType<typeof createEmailUndoApi>;

const primaryBtn = "h-12 w-full gap-2 bg-brand text-[15px] font-semibold text-brand-foreground hover:bg-brand/90";

function Header({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <div className="mb-6">
      <h1 className="text-[28px] font-bold leading-[34px] tracking-tight text-foreground">{title}</h1>
      {subtitle ? <p className="mt-2 text-[15px] leading-5 text-muted-foreground">{subtitle}</p> : null}
    </div>
  );
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function EmailUndoPage({ api: injected }: { api?: Api }) {
  const api = useMemo(() => injected ?? createEmailUndoApi(env.apiBaseUrl), [injected]);
  const { status, logout } = useAuth();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  // Read once; the address bar loses it below.
  const [token] = useState(() => new URLSearchParams(search).get("token") ?? "");
  const [info, setInfo] = useState<EmailUndoInfo | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ message: string; email: string | null } | null>(null);

  useEffect(() => {
    if (search) navigate(pathname, { replace: true });
  }, [navigate, pathname, search]);

  useEffect(() => {
    if (!token) {
      setInvalid("This link isn't complete. Open it again from the email, or contact support.");
      return;
    }
    let gone = false;
    void api.lookup(token).then((r) => {
      if (gone) return;
      const parsed = r.ok ? parseUndoInfo(r.data) : null;
      if (parsed) setInfo(parsed);
      else setInvalid(r.ok ? "We couldn't read this link. Please try again." : r.message);
    });
    return () => {
      gone = true;
    };
  }, [api, token]);

  const undo = async () => {
    setBusy(true);
    setError(null);
    const r = await api.undo(token);
    setBusy(false);
    if (r.ok) {
      setDone({ message: r.message, email: typeof r.data.email === "string" ? r.data.email : null });
      // Every session of the account is signed out by the server; this
      // browser's copy goes too (any account: undo is not signed-in work).
      forgetTrustedDevice();
      if (status === "authenticated") await logout();
    } else if (r.code === "LINK_INVALID") {
      setInvalid(r.message);
    } else {
      setError(r.message);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center py-6 text-center">
        <Check className="h-24 w-24 text-emerald-500 duration-500 animate-in zoom-in-50 fade-in-0" strokeWidth={2.5} aria-hidden />
        <h2 className="mt-4 text-[28px] font-bold leading-[34px] tracking-tight text-foreground">Your old email is back</h2>
        <p className="mt-2 text-[15px] leading-5 text-muted-foreground">{done.message}</p>
        {done.email ? <p className="mt-3 text-sm font-medium">{done.email}</p> : null}
        <Button asChild className={`${primaryBtn} mt-8`} size="lg">
          <Link to="/forgot-password">
            <KeyRound className="h-4 w-4" />
            Reset password
          </Link>
        </Button>
        <Link to="/login" className="mt-6 text-sm font-medium text-brand hover:underline">
          Sign in
        </Link>
      </div>
    );
  }

  if (invalid) {
    return (
      <div>
        <Header title="This link doesn't work" subtitle={invalid} />
        <Button asChild className={primaryBtn} size="lg">
          <Link to="/login">Back to Sign in</Link>
        </Button>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="flex justify-center py-16 text-muted-foreground" aria-label="Checking your link">
        <LoaderCircle className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const changedAt = formatDate(info.changedAt);
  const expiresAt = formatDate(info.expiresAt);
  return (
    <div>
      <Header
        title="Undo the email change"
        subtitle={`Your VERBLIZR email was changed${info.changedToMasked ? ` to ${info.changedToMasked}` : ""}${changedAt ? ` on ${changedAt}` : ""}. If that wasn't you, undo it here.`}
      />
      <div className="rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm leading-5">
        <p>
          Puts back <span className="font-semibold">{info.restoresEmail}</span>
        </p>
        <p className="mt-2 text-muted-foreground">{info.whatHappens}</p>
        {expiresAt ? <p className="mt-2 text-xs text-muted-foreground">This link works until {expiresAt}, once.</p> : null}
      </div>
      <div className="mt-4">
        <ServerError message={error} />
      </div>
      <Button type="button" className={`${primaryBtn} mt-4`} size="lg" disabled={busy} onClick={() => void undo()}>
        {busy ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
        Undo the change
      </Button>
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Changed it yourself? You don&rsquo;t need to do anything.
      </p>
    </div>
  );
}
